import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AlertOutlined,
  DollarCircleOutlined,
  EditOutlined,
  InboxOutlined,
  ReloadOutlined,
  ShopOutlined,
  ClockCircleOutlined,
  AppstoreOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { Table, Select, DatePicker, Button, Image, message } from "antd";
import dayjs from "dayjs";
import axios from "../../utils/axiosDash";

const STORE_STORAGE_KEY = "selectedStore";
const REFRESH_MS = 30000;

const EMPTY_COUNTS = { total: 0, cashier: 0, writer: 0, storeRoom: 0, hygiene: 0 };

// Values sent to the backend as `useCase`
const USE_CASES = [
  { value: "all", label: "All alerts" },
  { value: "cashier", label: "Cashier Alerts" },
  { value: "writer", label: "Writer Alerts" },
  { value: "storeroom", label: "Store Room Alerts" },
  { value: "hygiene", label: "Hygiene Alerts" },
];

// Keyed by the `type` returned from the backend
const CATEGORIES = {
  cashier: { label: "Cashier", icon: <DollarCircleOutlined />, color: "#DC2626", soft: "#FEF2F2" },
  writer: { label: "Writer", icon: <EditOutlined />, color: "#B45309", soft: "#FFFBEB" },
  storeRoom: { label: "Store room", icon: <InboxOutlined />, color: "#2F6FED", soft: "#EFF4FF" },
  hygiene: { label: "Hygiene", icon: <SafetyCertificateOutlined />, color: "#7C3AED", soft: "#F5F3FF" },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Today as YYYY-MM-DD from the browser's local date
const todayISO = () => dayjs().format("YYYY-MM-DD");

const isAllStores = (name) => !name || name === "All Stores";

// postgresCreatedAt is shown exactly as stored (no timezone conversion)
const formatTime = (value) => {
  if (!value) return { day: "—", clock: "" };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { day: "—", clock: "" };
  return {
    day: d.toLocaleDateString("en-IN", { timeZone: "UTC", day: "2-digit", month: "short", year: "numeric" }),
    clock: d.toLocaleTimeString("en-IN", { timeZone: "UTC", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }),
  };
};

const prettyType = (t) =>
  t ? String(t).replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase()) : "—";

// ---------------------------------------------------------------------------
// Presentational pieces
// ---------------------------------------------------------------------------

function StatCard({ label, value, icon, color, soft }) {
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-[#E6EAF0] bg-white p-5 shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
      <div
        className="flex h-11 w-11 items-center justify-center rounded-xl text-lg"
        style={{ background: soft, color }}
      >
        {icon}
      </div>
      <div>
        <p className="text-xs font-medium text-[#7C8398]">{label}</p>
        <p className="mt-0.5 text-2xl font-bold leading-none text-[#0B0F1F]">{value}</p>
      </div>
    </div>
  );
}

function EventCell({ record }) {
  const cat = CATEGORIES[record.type] || { label: record.type, icon: <AlertOutlined />, color: "#2F6FED", soft: "#EFF4FF" };
  return (
    <div className="flex items-start gap-3">
      <span
        className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm"
        style={{ background: cat.soft, color: cat.color }}
      >
        {cat.icon}
      </span>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-[#12172B]">{record.message}</p>
        <p className="mt-0.5 text-xs text-[#8A90A3]">
          {cat.label} · {prettyType(record.alertType)}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function AlertsIncidentManagement() {
  const location = useLocation();
  const navigate = useNavigate();

  // If we arrived from the bell drawer, start already focused on that alert
  // (avoids one wasted fetch with the default filters)
  const initialFocus = useRef(location.state?.focusAlert || null).current;

  const [storeName, setStoreName] = useState(
    localStorage.getItem(STORE_STORAGE_KEY) || "All Stores"
  );

  const [alerts, setAlerts] = useState([]);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [totalRows, setTotalRows] = useState(0);
  const [serverPage, setServerPage] = useState(1);
  const [restaurantOptions, setRestaurantOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [useCase, setUseCase] = useState(initialFocus?.sourceKey || "all");
  const [date, setDate] = useState(initialFocus?.date || todayISO());
  const [restaurantFilter, setRestaurantFilter] = useState("ALL");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortOrder, setSortOrder] = useState("desc"); // "desc" = newest first

  // Alert opened from the notification bell (null = normal list)
  const [focusAlert, setFocusAlert] = useState(initialFocus);

  // Ignores responses from outdated requests
  const requestRef = useRef(0);

  const isToday = date === todayISO();
  const showRestaurant = isAllStores(storeName);

  // Store changed from another tab / header selector
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORE_STORAGE_KEY) {
        setStoreName(e.newValue || "All Stores");
        setRestaurantFilter("ALL");
        setPage(1);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // An alert was clicked in the bell drawer (works on first load and while already on this page)
  useEffect(() => {
    const f = location.state?.focusAlert;
    if (!f) return;

    setFocusAlert(f);
    setUseCase(f.sourceKey);
    setDate(f.date);
    setRestaurantFilter("ALL");
    setPage(1);

    // Clear the router state so a refresh doesn't re-apply it
    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAlerts = useCallback(
    async (silent = false) => {
      const reqId = ++requestRef.current;
      if (!silent) setLoading(true);
      try {
        const response = await axios.post("/get-empire-alerts-data", {
          storeName,
          useCase, // all | cashier | writer | storeroom | hygiene
          date, // YYYY-MM-DD
          page,
          limit: pageSize,
          restaurant: showRestaurant ? restaurantFilter : "ALL",
          sort: sortOrder,
          alertId: focusAlert?.id || undefined, // only set when opened from the bell
        });

        if (reqId !== requestRef.current) return; // a newer request replaced this one

        if (response.data?.success) {
          const body = response.data;
          setAlerts(Array.isArray(body.data) ? body.data : []);
          setTotalRows(body.pagination?.total ?? 0);
          setServerPage(body.pagination?.page ?? 1);
          setRestaurantOptions(body.options?.restaurants || []);
          setCounts({
            total: body.meta?.total ?? 0,
            cashier: body.meta?.cashier ?? 0,
            writer: body.meta?.writer ?? 0,
            storeRoom: body.meta?.storeRoom ?? 0,
            hygiene: body.meta?.hygiene ?? 0,
          });
        } else {
          message.error(response.data?.message || "Failed to fetch alerts");
        }
      } catch (error) {
        if (reqId !== requestRef.current) return;
        console.error("Error fetching Empire alerts:", error);
        message.error(error.response?.data?.message || "Failed to fetch alerts");
      } finally {
        if (reqId === requestRef.current) setLoading(false);
      }
    },
    [storeName, useCase, date, page, pageSize, restaurantFilter, showRestaurant, sortOrder, focusAlert]
  );

  // Refetch when page, page size, sort, store, restaurant, use case, date or focus changes
  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // Auto-refresh only while viewing today (refreshes the current page)
  useEffect(() => {
    if (!isToday) return undefined;
    const t = setInterval(() => fetchAlerts(true), REFRESH_MS);
    return () => clearInterval(t);
  }, [fetchAlerts, isToday]);

  // ---- Filter handlers: every change goes back to page 1 and leaves focus mode ----
  const changeUseCase = (v) => {
    setFocusAlert(null);
    setUseCase(v);
    setPage(1);
  };
  const changeDate = (v) => {
    setFocusAlert(null);
    setDate(v);
    setRestaurantFilter("ALL"); // restaurant list is different for each day
    setPage(1);
  };
  const changeRestaurant = (v) => {
    setFocusAlert(null);
    setRestaurantFilter(v);
    setPage(1);
  };

  // Table pagination / sorting
  const handleTableChange = (pagination, _filters, sorter) => {
    const nextSort = sorter?.order === "ascend" ? "asc" : "desc";
    if (pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize);
      setPage(1);
    } else if (nextSort !== sortOrder) {
      setSortOrder(nextSort);
      setPage(1);
    } else {
      setPage(pagination.current);
    }
  };

  const restaurantSelectOptions = useMemo(
    () => [
      { value: "ALL", label: "All Restaurants" },
      ...restaurantOptions.map((r) => ({ value: r, label: r })),
    ],
    [restaurantOptions]
  );

  const columns = useMemo(
    () => [
      {
        title: "Event",
        dataIndex: "message",
        key: "event",
        width: 340,
        render: (_, record) => <EventCell record={record} />,
      },
      {
        title: "Store",
        dataIndex: "store",
        key: "store",
        width: 160,
        render: (v) => (
          <span className="inline-flex items-center gap-2 text-sm text-[#3B4152]">
            <ShopOutlined className="text-[#8A90A3]" />
            {v || "—"}
          </span>
        ),
      },
      {
        title: "Date / time",
        dataIndex: "postgresCreatedAt",
        key: "time",
        width: 180,
        sorter: true, // sorted on the server
        sortOrder: sortOrder === "asc" ? "ascend" : "descend",
        sortDirections: ["descend", "ascend"],
        render: (v) => {
          const t = formatTime(v);
          return (
            <div>
              <p className="text-sm font-medium text-[#12172B]">{t.day}</p>
              <p className="text-xs text-[#8A90A3]">{t.clock}</p>
            </div>
          );
        },
      },
      {
        title: "Snapshot",
        dataIndex: "image",
        key: "image",
        width: 120,
        align: "center",
        render: (src) =>
          src ? (
            <Image
              src={src}
              width={72}
              height={46}
              style={{ objectFit: "cover", borderRadius: 8 }}
              preview={{ mask: "View" }}
            />
          ) : (
            <span className="text-[#A0A6B5]">—</span>
          ),
      },
    ],
    [sortOrder]
  );

  const filtersActive =
    useCase !== "all" || !isToday || (showRestaurant && restaurantFilter !== "ALL") || !!focusAlert;

  return (
    <div className="min-h-screen w-full bg-[#F6F8FB] text-[#12172B]">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        .alerts-page, .alerts-page .ant-table, .alerts-page .ant-select, .alerts-page .ant-picker, .alerts-page .ant-btn {
          font-family: 'Inter', ui-sans-serif, system-ui, sans-serif;
        }
        .alerts-page .ant-table-thead > tr > th {
          background: #F9FAFC; color: #7C8398; font-size: 12px; font-weight: 600;
        }
        .alerts-page .ant-table-tbody > tr > td { padding: 14px 16px; }
        .alerts-page .ant-table-tbody > tr:hover > td { background: #F9FAFC !important; }
        .alerts-page .ant-table-tbody > tr.alerts-focused-row > td { background: #EFF4FF !important; }
      `}</style>

      <div className="alerts-page mx-auto max-w-[1440px] px-4 py-6 sm:px-6 lg:px-8">
        {/* ---------------- Header ---------------- */}
        <header className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#2F6FED] to-[#1E4FBE] shadow-md shadow-[#2F6FED]/25">
              <AlertOutlined className="text-lg text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold leading-none tracking-tight text-[#0B0F1F]">Alerts & Incident Management</h1>
              <p className="mt-1.5 text-xs text-[#8A90A3]">CogniVision · Empire Restaurant</p>
            </div>
          </div>
          <div className="flex items-center gap-2 self-start rounded-full bg-white px-3.5 py-2 text-sm font-medium text-[#2F6FED] ring-1 ring-[#BFD3FB] sm:self-auto">
            {isToday && <span className="h-2 w-2 animate-pulse rounded-full bg-[#12B76A]" />}
            <ClockCircleOutlined />
            {isToday ? "Live · refreshes every 30s" : `Viewing ${dayjs(date).format("DD MMM YYYY")}`}
          </div>
        </header>

        {/* ---------------- Stats ---------------- */}
        <section className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-5">
          <StatCard label="Total alerts" value={counts.total} icon={<AlertOutlined />} color="#2F6FED" soft="#EFF4FF" />
          <StatCard label="Cashier alerts" value={counts.cashier} icon={<DollarCircleOutlined />} color="#DC2626" soft="#FEF2F2" />
          <StatCard label="Writer alerts" value={counts.writer} icon={<EditOutlined />} color="#B45309" soft="#FFFBEB" />
          <StatCard label="Store room alerts" value={counts.storeRoom} icon={<InboxOutlined />} color="#0C8A54" soft="#ECFDF5" />
          <StatCard label="Hygiene alerts" value={counts.hygiene} icon={<SafetyCertificateOutlined />} color="#7C3AED" soft="#F5F3FF" />
        </section>

        {/* ---------------- Table card ---------------- */}
        <section className="overflow-hidden rounded-2xl border border-[#E6EAF0] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
          {/* Toolbar */}
          <div className="flex flex-col gap-3 border-b border-[#EEF1F5] p-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-[15px] font-semibold text-[#12172B]">Incident log</h2>
              <p className="text-xs text-[#8A90A3]">
                {totalRows.toLocaleString()} alerts
                {showRestaurant && restaurantFilter !== "ALL" ? ` · ${restaurantFilter}` : ""}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Select
                value={useCase}
                onChange={changeUseCase}
                options={USE_CASES}
                suffixIcon={<AppstoreOutlined />}
                style={{ width: 180 }}
              />
              {showRestaurant && restaurantOptions.length > 1 && (
                <Select
                  value={restaurantFilter}
                  onChange={changeRestaurant}
                  options={restaurantSelectOptions}
                  suffixIcon={<ShopOutlined />}
                  style={{ width: 200 }}
                  aria-label="Filter by restaurant"
                />
              )}
              <DatePicker
                value={dayjs(date)}
                allowClear={false}
                format="DD MMM YYYY"
                disabledDate={(d) => d && d.isAfter(dayjs(todayISO()), "day")}
                onChange={(d) => d && changeDate(d.format("YYYY-MM-DD"))}
              />
              <Button icon={<ReloadOutlined />} onClick={() => fetchAlerts()}>
                Refresh
              </Button>
              {filtersActive && (
                <Button
                  type="link"
                  onClick={() => {
                    setFocusAlert(null);
                    setUseCase("all");
                    setDate(todayISO());
                    setRestaurantFilter("ALL");
                    setPage(1);
                  }}
                >
                  Reset to today
                </Button>
              )}
            </div>
          </div>

          {/* Shown when an alert was opened from the notification bell */}
          {focusAlert && (
            <div className="flex items-center justify-between gap-3 border-b border-[#BFD3FB] bg-[#EFF4FF] px-4 py-2.5 text-sm text-[#1E4FBE]">
              <span>Showing the alert you opened from notifications.</span>
              <Button type="link" size="small" onClick={() => setFocusAlert(null)}>
                Show all alerts
              </Button>
            </div>
          )}

          <Table
            rowKey="id"
            columns={columns}
            dataSource={alerts}
            loading={loading}
            scroll={{ x: 1000 }}
            onChange={handleTableChange}
            rowClassName={(record) =>
              focusAlert && record.id === focusAlert.id ? "alerts-focused-row" : ""
            }
            pagination={{
              current: serverPage,
              pageSize,
              total: totalRows, // server total, so pages are fetched on click
              showSizeChanger: true,
              pageSizeOptions: [10, 25, 50],
              showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
              style: { padding: "0 16px" },
            }}
            locale={{
              emptyText: (
                <div className="py-10 text-sm text-[#A0A6B5]">
                  No alerts found for {isToday ? "today" : dayjs(date).format("DD MMM YYYY")}.
                </div>
              ),
            }}
          />
        </section>
      </div>
    </div>
  );
}